import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase, type PublishedJob } from '../lib/supabase'
import { cn } from '../lib/utils'
import { Briefcase, MapPin, Globe, Train, Code, Users, Plane, FileText, ExternalLink, Smile, GraduationCap, Cpu, UsersRound, Blocks, PiggyBank, Landmark, CircleArrowRight, Link, Palette, BarChart, ShieldCheck, Megaphone, DollarSign, Wrench, Heart, Coins, TrendingUp, X, HelpCircle, Sparkles, Navigation, Flag, AlertCircle } from 'lucide-react'
import { RiFlashlightFill, RiLinkedinFill, RiInformationLine } from '@remixicon/react'
import { JobFilters, type JobFiltersState } from './JobFilters'
import { useLocations } from '../hooks/useLocations'
import { useTeams } from '../hooks/useTeams'
import type { LocationSuggestion } from '../hooks/useLocationSearch'
import { searchLocations } from '../lib/locationMapping'
import { isWithinRadius } from '../lib/distance'
import { FilterBadgeClean } from './ui/filter-badge-clean'
import { pathToFilters, generateUrlPath, isLikelyLocation, fromKebabCase } from '../lib/urlFilters'
import { isSameCountry } from '../lib/countryNormalization'
import { useToast } from './ui/use-toast'

const JOBS_PER_PAGE = 50

// Toggle this to show/hide maintenance alert
const SHOW_MAINTENANCE_ALERT = false

// Helper function to map country codes to their regions
function getRegionForCountry(countryCode: string): string | null {
    const regions: Record<string, string> = {
        // North America
        'US': 'North America', 'CA': 'North America', 'MX': 'North America',
        // Latin America (Central America, Caribbean, South America)
        'CR': 'Latin America', 'PA': 'Latin America', 'GT': 'Latin America',
        'HN': 'Latin America', 'NI': 'Latin America', 'SV': 'Latin America',
        'BZ': 'Latin America', 'JM': 'Latin America', 'TT': 'Latin America',
        'BB': 'Latin America', 'DO': 'Latin America', 'CU': 'Latin America',
        'BR': 'Latin America', 'AR': 'Latin America', 'CL': 'Latin America',
        'CO': 'Latin America', 'PE': 'Latin America', 'VE': 'Latin America',
        'EC': 'Latin America', 'BO': 'Latin America', 'PY': 'Latin America',
        'UY': 'Latin America', 'GY': 'Latin America', 'SR': 'Latin America',
        // Europe (all European countries)
        'GB': 'Europe', 'IE': 'Europe', 'FR': 'Europe', 'DE': 'Europe',
        'ES': 'Europe', 'IT': 'Europe', 'PT': 'Europe', 'NL': 'Europe',
        'BE': 'Europe', 'LU': 'Europe', 'CH': 'Europe', 'AT': 'Europe',
        'DK': 'Europe', 'SE': 'Europe', 'NO': 'Europe', 'FI': 'Europe', 'IS': 'Europe',
        'PL': 'Europe', 'CZ': 'Europe', 'SK': 'Europe', 'HU': 'Europe',
        'RO': 'Europe', 'BG': 'Europe', 'EE': 'Europe', 'LV': 'Europe',
        'LT': 'Europe', 'BY': 'Europe', 'UA': 'Europe', 'MD': 'Europe',
        'GR': 'Europe', 'HR': 'Europe', 'SI': 'Europe', 'RS': 'Europe',
        'BA': 'Europe', 'MK': 'Europe', 'AL': 'Europe',
        // APAC (Asia + Pacific)
        'CN': 'APAC', 'JP': 'APAC', 'KR': 'APAC', 'TW': 'APAC',
        'HK': 'APAC', 'MN': 'APAC', 'SG': 'APAC', 'MY': 'APAC',
        'TH': 'APAC', 'VN': 'APAC', 'PH': 'APAC', 'ID': 'APAC',
        'MM': 'APAC', 'KH': 'APAC', 'LA': 'APAC', 'IN': 'APAC',
        'PK': 'APAC', 'BD': 'APAC', 'LK': 'APAC', 'NP': 'APAC',
        'AU': 'APAC', 'NZ': 'APAC', 'PG': 'APAC', 'FJ': 'APAC',
        // Middle East
        'AE': 'Middle East', 'SA': 'Middle East', 'IL': 'Middle East',
        'TR': 'Middle East', 'EG': 'Middle East', 'JO': 'Middle East',
        'QA': 'Middle East', 'KW': 'Middle East', 'OM': 'Middle East',
        'BH': 'Middle East', 'LB': 'Middle East', 'IQ': 'Middle East',
        // Africa
        'ZA': 'Africa', 'NG': 'Africa', 'KE': 'Africa', 'GH': 'Africa',
        'MA': 'Africa', 'TN': 'Africa', 'DZ': 'Africa', 'ET': 'Africa',
        'UG': 'Africa', 'TZ': 'Africa'
    }
    return regions[countryCode] || null
}

interface JobsGridProps {
    selectedLocation: LocationSuggestion | null
    onLocationSelect: (location: LocationSuggestion | null) => void
    onFeedbackClick?: () => void
}

function JobsGrid({ selectedLocation, onLocationSelect, onFeedbackClick }: JobsGridProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const [jobs, setJobs] = useState<PublishedJob[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [expandedJobId, setExpandedJobId] = useState<string | null>(null)
    const [showWorkSettingReasoning, setShowWorkSettingReasoning] = useState<string | null>(null)
    const [displayedJobsCount, setDisplayedJobsCount] = useState(JOBS_PER_PAGE)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [filters, setFilters] = useState<JobFiltersState>({
        workTypes: new Set(),
        hasSalary: false,
        seniorities: new Set(),
        teams: new Set(),
        roleAttributes: new Set(),
        compensation: new Set(),
        selectedLocation: null,
        jobTitleSearch: '',
        popularQuery: null,
        stages: new Set()
    })
    const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
    const [activeCompanyCount, setActiveCompanyCount] = useState<number>(0)
    const [showJobCountTooltip, setShowJobCountTooltip] = useState(false)
    const [showMaintenanceTooltip, setShowMaintenanceTooltip] = useState(false)
    const [isInitialMount, setIsInitialMount] = useState(true)
    const [hasLoadedFromUrl, setHasLoadedFromUrl] = useState(false)
    const [reportedJobs, setReportedJobs] = useState<Set<string>>(new Set())
    const [reportingJobs, setReportingJobs] = useState<Set<string>>(new Set())
    const [jobsPostedTodayCount, setJobsPostedTodayCount] = useState<number>(0)
    const [totalJobCount, setTotalJobCount] = useState<number>(0)
    const [needsFullDataset, setNeedsFullDataset] = useState(false)
    const [isLoadingFullDataset, setIsLoadingFullDataset] = useState(false)
    const [isWaitingForFullDataset, setIsWaitingForFullDataset] = useState(false)

    const { teams: availableTeams, loading: teamsLoading } = useTeams()
    const { toast } = useToast()

    // Initialize filters from URL on mount (wait for teams to load to avoid misidentifying them as locations)
    useEffect(() => {
        // Wait for teams to finish loading before parsing URL
        if (teamsLoading) return

        // Only parse URL once when teams finish loading
        if (hasLoadedFromUrl) return

        const pathname = location.pathname
        if (pathname === '/') {
            setHasLoadedFromUrl(true)
            setIsInitialMount(false)
            return
        }

        const segments = pathname.split('/').filter(Boolean)
        const parsedFilters = pathToFilters(segments)

        // Check if any segment might be a location
        const locationSegments = segments.filter(seg =>
            isLikelyLocation(seg, availableTeams)
        )

        if (locationSegments.length > 0) {
            // Try to find the location
            const locationName = fromKebabCase(locationSegments[0])
            const locationResults = searchLocations(locationName, 1)
            if (locationResults.length > 0) {
                onLocationSelect(locationResults[0])
            }
        }

        // Remove location segments from teams (they were incorrectly added by pathToFilters)
        const filteredTeams = new Set(parsedFilters.teams)
        locationSegments.forEach(seg => {
            const teamName = fromKebabCase(seg)
            filteredTeams.delete(teamName)
        })

        setFilters(prev => ({
            ...prev,
            workTypes: parsedFilters.workTypes || new Set(),
            seniorities: parsedFilters.seniorities || new Set(),
            teams: filteredTeams,
            stages: parsedFilters.stages || new Set(),
        }))

        // Mark that we've loaded from URL
        setHasLoadedFromUrl(true)
    }, [availableTeams, teamsLoading, hasLoadedFromUrl, location.pathname, onLocationSelect])

    // Sync selectedLocation from props with filters state
    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            selectedLocation: selectedLocation
        }))

        // Mark initial mount as complete after location is synced AND URL has been parsed
        if (isInitialMount && hasLoadedFromUrl) {
            setIsInitialMount(false)
        }
    }, [selectedLocation, isInitialMount, hasLoadedFromUrl])

    // Update URL when filters change (but not on initial mount to prevent race conditions)
    useEffect(() => {
        // Skip URL updates during initial mount to prevent losing location from URL
        if (isInitialMount) {
            return
        }

        const newPath = generateUrlPath(filters)
        if (newPath !== location.pathname) {
            navigate(newPath, { replace: true })
        }
    }, [filters, navigate, isInitialMount, location.pathname])

    useEffect(() => {
        fetchInitialData()
    }, [])

    // Trigger full dataset load when filters are applied
    useEffect(() => {
        const hasActiveFilters =
            filters.workTypes.size > 0 ||
            filters.seniorities.size > 0 ||
            filters.teams.size > 0 ||
            filters.roleAttributes.size > 0 ||
            filters.compensation.size > 0 ||
            filters.selectedLocation !== null ||
            filters.jobTitleSearch !== '' ||
            filters.popularQuery !== null ||
            filters.stages.size > 0

        if (hasActiveFilters && !needsFullDataset && jobs.length < 1000) {
            setNeedsFullDataset(true)
            fetchFullDataset()
        }
    }, [filters, needsFullDataset, jobs.length])

    // Special handling for location filter: wait for full dataset before showing results
    useEffect(() => {
        async function ensureFullDatasetForLocation() {
            // Only care about location filter
            if (!filters.selectedLocation) {
                setIsWaitingForFullDataset(false)
                return
            }

            // If we already have full dataset or it's loading, no need to wait
            if (needsFullDataset || isLoadingFullDataset) {
                // If it's loading, mark that we're waiting
                if (isLoadingFullDataset) {
                    setIsWaitingForFullDataset(true)
                } else {
                    setIsWaitingForFullDataset(false)
                }
                return
            }

            // Check if we have the full dataset (more than 1000 jobs means we fetched all)
            if (jobs.length >= 1000) {
                setIsWaitingForFullDataset(false)
                return
            }

            // We need to load the full dataset - show loading state
            setIsWaitingForFullDataset(true)
            try {
                await fetchFullDataset()
            } catch (err) {
                console.error('Failed to load full dataset for location filter:', err)
            } finally {
                setIsWaitingForFullDataset(false)
            }
        }

        ensureFullDatasetForLocation()
    }, [filters.selectedLocation, needsFullDataset, isLoadingFullDataset, jobs.length])

    async function fetchActiveCompanyCount() {
        try {
            const { count, error: countError } = await supabase
                .from('companies')
                .select('*', { count: 'exact', head: true })
                .eq('is_active', true)

            if (countError) {
                console.error('Error fetching company count:', countError)
            } else {
                setActiveCompanyCount(count || 0)
            }
        } catch (err) {
            console.error('Error fetching active company count:', err)
        }
    }

    async function fetchJobsPostedTodayCount() {
        try {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            const tomorrow = new Date(today)
            tomorrow.setDate(tomorrow.getDate() + 1)

            const { count, error: countError } = await supabase
                .from('published_jobs')
                .select('*', { count: 'exact', head: true })
                .gte('created_at', today.toISOString())
                .lt('created_at', tomorrow.toISOString())

            if (countError) {
                console.error('Error fetching jobs posted today count:', countError)
            } else {
                setJobsPostedTodayCount(count || 0)
            }
        } catch (err) {
            console.error('Error fetching jobs posted today count:', err)
        }
    }

    async function fetchTotalJobCount() {
        try {
            // Calculate cutoff date (12 days ago)
            const cutoffDate = new Date()
            cutoffDate.setDate(cutoffDate.getDate() - 12)

            const { count, error: countError } = await supabase
                .from('published_jobs')
                .select('*', { count: 'exact', head: true })
                .gte('created_at', cutoffDate.toISOString())

            if (countError) {
                console.error('Error fetching total job count:', countError)
            } else {
                setTotalJobCount(count || 0)
            }
        } catch (err) {
            console.error('Error fetching total job count:', err)
        }
    }

    async function fetchInitialData() {
        try {
            setLoading(true)

            // Calculate cutoff date (12 days ago)
            const cutoffDate = new Date()
            cutoffDate.setDate(cutoffDate.getDate() - 12)

            // Run all initial queries in parallel for faster load
            const [jobsResult, _, __, ___] = await Promise.all([
                // 1. Fetch initial 50 jobs for display (last 12 days only)
                supabase
                    .from('published_jobs')
                    .select('*')
                    .gte('created_at', cutoffDate.toISOString())
                    .order('created_at', { ascending: false })
                    .limit(50),
                // 2. Get count of jobs posted today (fast count query)
                fetchJobsPostedTodayCount(),
                // 3. Get active company count
                fetchActiveCompanyCount(),
                // 4. Get total job count (fast count query)
                fetchTotalJobCount()
            ])

            if (jobsResult.error) throw jobsResult.error
            setJobs(jobsResult.data || [])
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch jobs')
            console.error('Error fetching initial data:', err)
        } finally {
            setLoading(false)
        }
    }

    async function fetchFullDataset(): Promise<void> {
        if (isLoadingFullDataset) return

        try {
            setIsLoadingFullDataset(true)

            // Calculate cutoff date (12 days ago)
            const cutoffDate = new Date()
            cutoffDate.setDate(cutoffDate.getDate() - 12)

            const batchSize = 1000
            const concurrentBatches = 5 // Fetch 5 batches at a time to avoid overwhelming the server

            // Calculate how many batches we need based on totalJobCount
            const totalBatches = Math.ceil(totalJobCount / batchSize)

            const allJobs: PublishedJob[] = []

            // Fetch in chunks of concurrent batches
            for (let chunkStart = 0; chunkStart < totalBatches; chunkStart += concurrentBatches) {
                const chunkEnd = Math.min(chunkStart + concurrentBatches, totalBatches)
                const batchPromises = []

                // Create promises for this chunk
                for (let i = chunkStart; i < chunkEnd; i++) {
                    const from = i * batchSize
                    const to = from + batchSize - 1

                    batchPromises.push(
                        supabase
                            .from('published_jobs')
                            .select('*')
                            .gte('created_at', cutoffDate.toISOString())
                            .order('created_at', { ascending: false })
                            .range(from, to)
                    )
                }

                // Wait for this chunk to complete
                const results = await Promise.all(batchPromises)

                // Check for errors
                const errorResult = results.find(r => r.error)
                if (errorResult?.error) throw errorResult.error

                // Add jobs from this chunk
                const chunkJobs = results.flatMap(r => r.data || [])
                allJobs.push(...chunkJobs)
            }

            setJobs(allJobs)
            setNeedsFullDataset(true) // Mark that we now have the full dataset
        } catch (err) {
            console.error('Error fetching full dataset:', err)
            throw err // Re-throw to allow callers to handle errors
        } finally {
            setIsLoadingFullDataset(false)
        }
    }

    function formatSalary(min: number | null, max: number | null, currency: string | null): string {
        if (!min && !max) return ''
        const curr = currency || 'USD'
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: curr,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        })

        if (min && max) {
            return `${formatter.format(min)} - ${formatter.format(max)}`
        }
        return min ? `${formatter.format(min)}+` : `Up to ${formatter.format(max!)}`
    }

    function formatPostedDate(dateString: string): string {
        const date = new Date(dateString)
        const now = new Date()
        const diffInMs = now.getTime() - date.getTime()
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInMinutes < 60) return diffInMinutes === 0 ? 'now' : `${diffInMinutes}m`
        if (diffInHours < 24) return `${diffInHours}h`
        if (diffInDays === 0) return 'today'
        if (diffInDays < 7) return `${diffInDays}d`
        if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w`

        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }

    function formatLocation(job: PublishedJob): string {
        // For remote roles, prioritize display based on remote_eligibility
        if (job.work_setting?.toLowerCase() === 'remote') {
            // If remote_eligibility is 'region', prioritize showing regions
            if (job.remote_eligibility === 'region' && job.remote_regions && job.remote_regions.length > 0) {
                if (job.remote_regions.length === 1) return job.remote_regions[0]
                if (job.remote_regions.length === 2) return `${job.remote_regions[0]}, ${job.remote_regions[1]}`
                if (job.remote_regions.length === 3) return `${job.remote_regions[0]}, ${job.remote_regions[1]}, ${job.remote_regions[2]}`
                if (job.remote_regions.length === 4) return `${job.remote_regions[0]}, ${job.remote_regions[1]}, ${job.remote_regions[2]}, ${job.remote_regions[3]}`
                return `${job.remote_regions.slice(0, 4).join(', ')} +${job.remote_regions.length - 4}`
            }

            // If remote_eligibility is 'country', prioritize showing countries
            if (job.remote_eligibility === 'country' && job.remote_countries && job.remote_countries.length > 0) {
                if (job.remote_countries.length === 1) return job.remote_countries[0]
                if (job.remote_countries.length === 2) return `${job.remote_countries[0]}, ${job.remote_countries[1]}`
                if (job.remote_countries.length === 3) return `${job.remote_countries[0]}, ${job.remote_countries[1]}, ${job.remote_countries[2]}`
                if (job.remote_countries.length === 4) return `${job.remote_countries[0]}, ${job.remote_countries[1]}, ${job.remote_countries[2]}, ${job.remote_countries[3]}`
                return `${job.remote_countries.slice(0, 4).join(', ')} +${job.remote_countries.length - 4}`
            }

            // If remote_eligibility is 'state', prioritize showing states
            if (job.remote_eligibility === 'state' && job.remote_states && job.remote_states.length > 0) {
                if (job.remote_states.length === 1) return job.remote_states[0]
                if (job.remote_states.length === 2) return `${job.remote_states[0]}, ${job.remote_states[1]}`
                if (job.remote_states.length === 3) return `${job.remote_states[0]}, ${job.remote_states[1]}, ${job.remote_states[2]}`
                if (job.remote_states.length === 4) return `${job.remote_states[0]}, ${job.remote_states[1]}, ${job.remote_states[2]}, ${job.remote_states[3]}`
                return `${job.remote_states.slice(0, 4).join(', ')} +${job.remote_states.length - 4}`
            }

            // Fallback: check in order of specificity (states > countries > regions)
            if (job.remote_states && job.remote_states.length > 0) {
                if (job.remote_states.length === 1) return job.remote_states[0]
                if (job.remote_states.length === 2) return `${job.remote_states[0]}, ${job.remote_states[1]}`
                if (job.remote_states.length === 3) return `${job.remote_states[0]}, ${job.remote_states[1]}, ${job.remote_states[2]}`
                if (job.remote_states.length === 4) return `${job.remote_states[0]}, ${job.remote_states[1]}, ${job.remote_states[2]}, ${job.remote_states[3]}`
                return `${job.remote_states.slice(0, 4).join(', ')} +${job.remote_states.length - 4}`
            }
            if (job.remote_countries && job.remote_countries.length > 0) {
                if (job.remote_countries.length === 1) return job.remote_countries[0]
                if (job.remote_countries.length === 2) return `${job.remote_countries[0]}, ${job.remote_countries[1]}`
                if (job.remote_countries.length === 3) return `${job.remote_countries[0]}, ${job.remote_countries[1]}, ${job.remote_countries[2]}`
                if (job.remote_countries.length === 4) return `${job.remote_countries[0]}, ${job.remote_countries[1]}, ${job.remote_countries[2]}, ${job.remote_countries[3]}`
                return `${job.remote_countries.slice(0, 4).join(', ')} +${job.remote_countries.length - 4}`
            }
            if (job.remote_regions && job.remote_regions.length > 0) {
                if (job.remote_regions.length === 1) return job.remote_regions[0]
                if (job.remote_regions.length === 2) return `${job.remote_regions[0]}, ${job.remote_regions[1]}`
                if (job.remote_regions.length === 3) return `${job.remote_regions[0]}, ${job.remote_regions[1]}, ${job.remote_regions[2]}`
                if (job.remote_regions.length === 4) return `${job.remote_regions[0]}, ${job.remote_regions[1]}, ${job.remote_regions[2]}, ${job.remote_regions[3]}`
                return `${job.remote_regions.slice(0, 4).join(', ')} +${job.remote_regions.length - 4}`
            }
            return 'Maybe Global'
        }

        // For hybrid/onsite, show city + state (US/Canada) or city + country (others)
        if (job.city && job.city.length > 0 && job.country && job.country.length > 0) {
            const isUSorCanada = job.country[0] === 'United States' || job.country[0] === 'Canada' ||
                                  job.country[0] === 'US' || job.country[0] === 'USA' || job.country[0] === 'CA'

            if (isUSorCanada && job.state && job.state.length > 0) {
                // US/Canada: show city + state
                if (job.city.length === 1) return `${job.city[0]}, ${job.state[0]}`

                // Check if all cities are in the same state
                const allSameState = job.state.every(state => state === job.state![0])
                if (job.city.length === 2) {
                    if (allSameState) {
                        return `${job.city[0]} or ${job.city[1]}, ${job.state[0]}`
                    }
                    return `${job.city[0]}, ${job.state[0]} or ${job.city[1]}, ${job.state[1] || job.state[0]}`
                }
                return `${job.city[0]}, ${job.state[0]} +${job.city.length - 1}`
            } else {
                // Other countries: show city + country
                if (job.city.length === 1) return `${job.city[0]}, ${job.country[0]}`

                // Check if all cities are in the same country
                const allSameCountry = job.country.every(country => country === job.country![0])
                if (job.city.length === 2) {
                    if (allSameCountry) {
                        return `${job.city[0]} or ${job.city[1]}, ${job.country[0]}`
                    }
                    return `${job.city[0]}, ${job.country[0]} or ${job.city[1]}, ${job.country[1] || job.country[0]}`
                }
                return `${job.city[0]}, ${job.country[0]} +${job.city.length - 1}`
            }
        }

        // Fallback: just city if available
        if (job.city && job.city.length > 0) {
            if (job.city.length === 1) return job.city[0]
            if (job.city.length === 2) return `${job.city[0]}, ${job.city[1]}`
            return `${job.city[0]}, ${job.city[1]} +${job.city.length - 2}`
        }

        // For hybrid/onsite jobs: if no city but has country, show country (handles city-states like Singapore)
        if ((job.work_setting?.toLowerCase() === 'hybrid' || job.work_setting?.toLowerCase() === 'on-site') &&
            job.country && job.country.length > 0) {
            if (job.country.length === 1) return job.country[0]
            if (job.country.length === 2) return `${job.country[0]} or ${job.country[1]}`
            return `${job.country[0]} +${job.country.length - 1}`
        }

        // Fallback: check for remote_countries/states/regions even if work_setting isn't set (cap at 5+)
        if (job.remote_states && job.remote_states.length > 0) {
            if (job.remote_states.length === 1) return job.remote_states[0]
            if (job.remote_states.length === 2) return `${job.remote_states[0]}, ${job.remote_states[1]}`
            if (job.remote_states.length === 3) return `${job.remote_states[0]}, ${job.remote_states[1]}, ${job.remote_states[2]}`
            if (job.remote_states.length === 4) return `${job.remote_states[0]}, ${job.remote_states[1]}, ${job.remote_states[2]}, ${job.remote_states[3]}`
            return `${job.remote_states.slice(0, 4).join(', ')} +${job.remote_states.length - 4}`
        }
        if (job.remote_countries && job.remote_countries.length > 0) {
            if (job.remote_countries.length === 1) return job.remote_countries[0]
            if (job.remote_countries.length === 2) return `${job.remote_countries[0]}, ${job.remote_countries[1]}`
            if (job.remote_countries.length === 3) return `${job.remote_countries[0]}, ${job.remote_countries[1]}, ${job.remote_countries[2]}`
            if (job.remote_countries.length === 4) return `${job.remote_countries[0]}, ${job.remote_countries[1]}, ${job.remote_countries[2]}, ${job.remote_countries[3]}`
            return `${job.remote_countries.slice(0, 4).join(', ')} +${job.remote_countries.length - 4}`
        }
        if (job.remote_regions && job.remote_regions.length > 0) {
            if (job.remote_regions.length === 1) return job.remote_regions[0]
            if (job.remote_regions.length === 2) return `${job.remote_regions[0]}, ${job.remote_regions[1]}`
            if (job.remote_regions.length === 3) return `${job.remote_regions[0]}, ${job.remote_regions[1]}, ${job.remote_regions[2]}`
            if (job.remote_regions.length === 4) return `${job.remote_regions[0]}, ${job.remote_regions[1]}, ${job.remote_regions[2]}, ${job.remote_regions[3]}`
            return `${job.remote_regions.slice(0, 4).join(', ')} +${job.remote_regions.length - 4}`
        }

        // Final fallback based on work setting
        if (job.work_setting?.toLowerCase() === 'remote') return 'Maybe Global'
        if (job.work_setting?.toLowerCase() === 'hybrid') return 'N/A'
        return 'Location TBD'
    }

    function getCompanyInitials(companyName: string): string {
        const words = companyName.trim().split(/\s+/)
        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase()
        }
        return (words[0][0] + words[1][0]).toUpperCase()
    }

    function handleImageError(jobId: string) {
        setFailedImages(prev => new Set(prev).add(jobId))
    }

    function formatRemoteEligibility(eligibility: string | null): string {
        if (!eligibility) return ''

        switch (eligibility) {
            case 'state':
                return 'Remote from specific states'
            case 'country':
                return 'Remote from specific countries'
            case 'region':
                return 'Remote from specific regions'
            case 'global':
                return 'Remote from anywhere'
            case 'none':
                return 'Not remote eligible'
            default:
                return eligibility.charAt(0).toUpperCase() + eligibility.slice(1)
        }
    }

    function formatFundingStage(stage: string | null): string {
        if (!stage) return ''

        // Handle series rounds: series-a, series-b, series-c, etc.
        if (stage.toLowerCase().startsWith('series')) {
            const parts = stage.split('-')
            if (parts.length === 2) {
                return `Series ${parts[1].toUpperCase()}`
            }
        }

        // Capitalize first letter for other stages
        return stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase()
    }

    function getJobFamilyIcon(family: string) {
        const familyLower = family.toLowerCase()

        if (familyLower.includes('engineering') || familyLower.includes('software') || familyLower.includes('developer')) {
            return <Code className="w-3 h-3" />
        }
        if (familyLower.includes('design') || familyLower.includes('product design')) {
            return <Palette className="w-3 h-3" />
        }
        if (familyLower.includes('data') || familyLower.includes('analytics')) {
            return <BarChart className="w-3 h-3" />
        }
        if (familyLower.includes('product') || familyLower.includes('product management')) {
            return <Wrench className="w-3 h-3" />
        }
        if (familyLower.includes('marketing')) {
            return <Megaphone className="w-3 h-3" />
        }
        if (familyLower.includes('finance')) {
            return <TrendingUp className="w-3 h-3" />
        }
        if (familyLower.includes('revenue')) {
            return <Coins className="w-3 h-3" />
        }
        if (familyLower.includes('sales') || familyLower.includes('business development')) {
            return <DollarSign className="w-3 h-3" />
        }
        if (familyLower.includes('security')) {
            return <ShieldCheck className="w-3 h-3" />
        }
        if (familyLower.includes('people') || familyLower.includes('hr')) {
            return <Users className="w-3 h-3" />
        }
        if (familyLower.includes('support') || familyLower.includes('customer success') || familyLower.includes('customer experience')) {
            return <Heart className="w-3 h-3" />
        }

        return <Briefcase className="w-3 h-3" />
    }

    // Handle reporting incorrect job/company data
    async function handleReportJob(job: PublishedJob) {
        // Prevent duplicate submissions
        if (reportedJobs.has(job.id) || reportingJobs.has(job.id)) {
            return
        }

        setReportingJobs(prev => new Set(prev).add(job.id))

        try {
            const response = await fetch('/api/report-job', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    jobId: job.id,
                    jobTitle: job.job_title,
                    companyName: job.company_name_full,
                    jobUrl: job.job_url,
                })
            }).catch((networkError) => {
                // Catch network-level errors (CORS, connection refused, etc.)
                console.error('[Report Job] Network error:', networkError)
                throw new Error('Network request failed')
            })

            if (response.ok) {
                setReportedJobs(prev => new Set(prev).add(job.id))
                toast({
                    description: "Thanks! We'll take it from here 🫡",
                })
            } else {
                const errorText = await response.text().catch(() => 'Unknown error')
                console.error('[Report Job] HTTP error:', response.status, errorText)
                toast({
                    title: "Failed to submit report",
                    description: "Please try again later.",
                    variant: "destructive",
                })
            }
        } catch (error) {
            // Catch all other errors
            console.error('[Report Job] Error:', error instanceof Error ? error.message : error)
            toast({
                title: "Failed to submit report",
                description: "Please try again later.",
                variant: "destructive",
            })
        } finally {
            setReportingJobs(prev => {
                const newSet = new Set(prev)
                newSet.delete(job.id)
                return newSet
            })
        }
    }

    // Reset pagination when filters change
    useEffect(() => {
        setDisplayedJobsCount(JOBS_PER_PAGE)
    }, [filters])

    // Filter jobs based on current filters
    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            // Filter by job title search
            if (filters.jobTitleSearch) {
                const searchLower = filters.jobTitleSearch.toLowerCase()
                const titleMatch = job.job_title?.toLowerCase().includes(searchLower)
                if (!titleMatch) {
                    return false
                }
            }

            // Filter by work type
            if (filters.workTypes.size > 0) {
                const workSetting = job.work_setting?.toLowerCase()
                if (!workSetting || !filters.workTypes.has(workSetting)) {
                    return false
                }
            }

            // Filter by seniority
            if (filters.seniorities.size > 0) {
                if (!job.seniority || !filters.seniorities.has(job.seniority.toLowerCase())) {
                    return false
                }
            }

            // Filter by team
            if (filters.teams.size > 0) {
                if (!job.job_family || !filters.teams.has(job.job_family)) {
                    return false
                }
            }

            // Filter by stage
            if (filters.stages.size > 0) {
                if (!job.funding_stage) {
                    return false
                }
                // Normalize the job's funding stage to match URL format
                const normalizedStage = job.funding_stage.toLowerCase()
                if (!filters.stages.has(normalizedStage)) {
                    return false
                }
            }

            // Filter by role attributes
            if (filters.roleAttributes.size > 0) {
                const hasAllAttributes = Array.from(filters.roleAttributes).every(attr => {
                    return job[attr as keyof PublishedJob] === true
                })
                if (!hasAllAttributes) {
                    return false
                }
            }

            // Filter by compensation
            if (filters.compensation.size > 0) {
                const hasAnyCompensation = Array.from(filters.compensation).some(comp => {
                    // Special handling for salary - check if job has salary_min or salary_max
                    if (comp === 'salary') {
                        return job.salary_min !== null || job.salary_max !== null
                    }
                    return job[comp as keyof PublishedJob] === true
                })
                if (!hasAnyCompensation) {
                    return false
                }
            }

            // Filter by popular query
            if (filters.popularQuery) {
                switch (filters.popularQuery) {
                    case 'recent_funding': {
                        // Companies with funding in last 60 days
                        if (!job.last_funding_date) return false
                        const fundingDate = new Date(job.last_funding_date)
                        const sixtyDaysAgo = new Date()
                        sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
                        if (fundingDate < sixtyDaysAgo) return false
                        break
                    }
                    case 'early_stage': {
                        // Jobs at companies up to Series C AND up to 51-200 employee range
                        const earlyStages = ['pre-seed', 'seed', 'series-a', 'series-b', 'series-c']
                        const earlyEmployeeRanges = ['1-10', '11-50', '51-200']
                        const hasEarlyStage = job.funding_stage && earlyStages.includes(job.funding_stage.toLowerCase())
                        const hasEarlySize = job.employee_range && earlyEmployeeRanges.includes(job.employee_range)
                        if (!hasEarlyStage || !hasEarlySize) return false
                        break
                    }
                    case 'remote_us': {
                        // Remote jobs in United States
                        if (job.work_setting?.toLowerCase() !== 'remote') return false
                        if (!job.remote_countries?.some(c => c.toLowerCase() === 'united states')) return false
                        break
                    }
                    case 'ai_jobs': {
                        // Jobs with "AI" as a whole word in title
                        const titleLower = job.job_title?.toLowerCase()
                        if (!titleLower) return false
                        // Match "AI" as a whole word (with word boundaries)
                        const aiPattern = /\bai\b/
                        if (!aiPattern.test(titleLower)) return false
                        break
                    }
                    case 'support_engineer': {
                        // job_family = support AND "engineer" in job title
                        if (!job.job_family?.toLowerCase().includes('support')) return false
                        if (!job.job_title?.toLowerCase().includes('engineer')) return false
                        break
                    }
                }
            }

            // Filter by location - user's location determines which jobs they can work
            if (filters.selectedLocation) {
                const location = filters.selectedLocation
                const locationName = location.name.toLowerCase()
                const locationCountry = location.country.toLowerCase()

                let canWorkThisJob = false

                // Check if user can work onsite/hybrid jobs at this location
                if (location.type === 'city') {
                    // User is in a city - can work jobs in that city (exact match)
                    canWorkThisJob = job.city?.some(city => city.toLowerCase() === locationName) || false

                    // DISTANCE-BASED MATCHING: If user has coordinates, check nearby cities (within 100 miles)
                    if (!canWorkThisJob && location.latitude && location.longitude) {
                        canWorkThisJob = job.city?.some(jobCity => {
                            // Get coordinates for the job's city
                            const jobCityInfo = searchLocations(jobCity, 1)[0]
                            if (jobCityInfo?.latitude && jobCityInfo?.longitude) {
                                return isWithinRadius(
                                    location.latitude,
                                    location.longitude,
                                    jobCityInfo.latitude,
                                    jobCityInfo.longitude,
                                    100 // 100 miles radius
                                )
                            }
                            return false
                        }) || false
                    }

                    // Also can work remote jobs
                    // If the job has remote_states specified, user's state must be in that list
                    // Otherwise, check if job is available to user's country
                    if (!canWorkThisJob && location.state) {
                        const userState = location.state.toLowerCase()
                        if (job.remote_states && job.remote_states.length > 0) {
                            canWorkThisJob = job.remote_states.some(state => state.toLowerCase() === userState)
                        } else {
                            canWorkThisJob = job.remote_countries?.some(country => isSameCountry(country, location.country)) || false
                        }
                    } else if (!canWorkThisJob) {
                        // If we don't have state info, fall back to country check
                        canWorkThisJob = job.remote_countries?.some(country => isSameCountry(country, location.country)) || false
                    }

                    // Also can work remote jobs available to their region (e.g., Europe, APAC)
                    if (!canWorkThisJob && location.countryCode) {
                        const userRegion = getRegionForCountry(location.countryCode)
                        if (userRegion) {
                            canWorkThisJob = job.remote_regions?.some(region => region.toLowerCase() === userRegion.toLowerCase()) || false
                        }
                    }

                    // Also can work remote jobs with global eligibility
                    if (!canWorkThisJob) {
                        canWorkThisJob = job.remote_eligibility === 'global'
                    }
                } else if (location.type === 'state') {
                    // User is in a state - can work jobs in that state
                    canWorkThisJob = job.state?.some(state => state.toLowerCase() === locationName) || false

                    // Also can work remote jobs available to their state
                    if (!canWorkThisJob) {
                        canWorkThisJob = job.remote_states?.some(state => state.toLowerCase() === locationName) || false
                    }

                    // Also can work remote jobs available to their country
                    if (!canWorkThisJob) {
                        canWorkThisJob = job.remote_countries?.some(country => isSameCountry(country, location.country)) || false
                    }

                    // Also can work remote jobs available to their region (e.g., Europe, APAC)
                    if (!canWorkThisJob && location.countryCode) {
                        const userRegion = getRegionForCountry(location.countryCode)
                        if (userRegion) {
                            canWorkThisJob = job.remote_regions?.some(region => region.toLowerCase() === userRegion.toLowerCase()) || false
                        }
                    }

                    // Also can work remote jobs with global eligibility
                    if (!canWorkThisJob) {
                        canWorkThisJob = job.remote_eligibility === 'global'
                    }
                } else if (location.type === 'country') {
                    // User is in a country - can work jobs in that country
                    canWorkThisJob = job.country?.some(country => isSameCountry(country, location.name)) || false

                    // Also can work remote jobs available to their country
                    if (!canWorkThisJob) {
                        canWorkThisJob = job.remote_countries?.some(country => isSameCountry(country, location.name)) || false
                    }

                    // Also can work remote jobs available to their region (e.g., Europe, APAC)
                    if (!canWorkThisJob && location.countryCode) {
                        const userRegion = getRegionForCountry(location.countryCode)
                        if (userRegion) {
                            canWorkThisJob = job.remote_regions?.some(region => region.toLowerCase() === userRegion.toLowerCase()) || false
                        }
                    }

                    // Also can work remote jobs with global eligibility
                    if (!canWorkThisJob) {
                        canWorkThisJob = job.remote_eligibility === 'global'
                    }
                }

                if (!canWorkThisJob) {
                    return false
                }
            }

            return true
        })
    }, [jobs, filters])

    // Get currently displayed jobs (paginated)
    const displayedJobs = useMemo(() => {
        return filteredJobs.slice(0, displayedJobsCount)
    }, [filteredJobs, displayedJobsCount])

    // Update page title with job counter
    useEffect(() => {
        if (jobsPostedTodayCount > 0) {
            document.title = `${jobsPostedTodayCount} new ${jobsPostedTodayCount === 1 ? 'job' : 'jobs'} today | signed`
        } else {
            document.title = 'signed: new jobs for you'
        }

        // Cleanup: restore original title when component unmounts
        return () => {
            document.title = 'signed: new jobs for you'
        }
    }, [jobsPostedTodayCount])

    // Format today's date for tooltip
    const todayFormatted = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

    const hasMore = displayedJobsCount < filteredJobs.length

    // Load more jobs callback
    const loadMoreJobs = () => {
        if (isLoadingMore || !hasMore) return

        setIsLoadingMore(true)
        // Simulate slight delay for smooth UX
        setTimeout(() => {
            setDisplayedJobsCount(prev => prev + JOBS_PER_PAGE)
            setIsLoadingMore(false)
        }, 300)
    }

    if (loading) {
        return (
            <div className="flex flex-col gap-3 p-4 max-w-4xl mx-auto">
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="p-4 rounded-xl border border-gray-100 bg-white animate-pulse"
                    >
                        <div className="h-20 bg-gray-100 rounded"></div>
                    </div>
                ))}
            </div>
        )
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto p-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
                    Error: {error}
                </div>
            </div>
        )
    }

    if (jobs.length === 0) {
        return (
            <div className="max-w-7xl mx-auto p-4">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
                    No jobs available yet. Check back soon!
                </div>
            </div>
        )
    }

    return (
        <div className="max-w-4xl mx-auto px-3 sm:px-4">
            {/* Filter Bar */}
            <div className="bg-white py-4 sm:py-6">
                <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4">
                    <JobFilters
                        filters={filters}
                        onFiltersChange={setFilters}
                        availableTeams={availableTeams}
                        teamsLoading={teamsLoading}
                        selectedLocation={selectedLocation}
                        onLocationSelect={onLocationSelect}
                        onFeedbackClick={onFeedbackClick}
                        activeCompanyCount={activeCompanyCount}
                    />

                </div>
            </div>

            {/* Spacer to prevent job list from jumping */}
            <div className="h-2"></div>

            {/* Jobs List Container */}
            <div className="space-y-4">
            {/* Section Header */}
            <div className="flex items-center justify-between">
                {(filteredJobs.length > 0 || totalJobCount > 0) ? (
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-medium text-gray-900">New Opportunities</h2>
                        <div className="relative">
                            <span
                                className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-sm font-medium transition-colors hover:bg-gray-200 cursor-pointer"
                                onMouseEnter={() => setShowJobCountTooltip(true)}
                                onMouseLeave={() => setShowJobCountTooltip(false)}
                            >
                                {needsFullDataset && jobs.length > 50 ? filteredJobs.length : totalJobCount}
                            </span>
                            {showJobCountTooltip && (
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 pointer-events-none">
                                    <div className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                                        {jobsPostedTodayCount} new {jobsPostedTodayCount === 1 ? 'job' : 'jobs'} {todayFormatted}
                                        <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                        {SHOW_MAINTENANCE_ALERT && (
                            <div className="relative">
                                <span
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200 animate-pulse hover:animate-none cursor-pointer"
                                    onMouseEnter={() => setShowMaintenanceTooltip(true)}
                                    onMouseLeave={() => setShowMaintenanceTooltip(false)}
                                >
                                    <AlertCircle className="w-3.5 h-3.5" />
                                    Maintenance in progress
                                </span>
                                {showMaintenanceTooltip && (
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 pointer-events-none">
                                        <div className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap">
                                            Fixing job data logic :)
                                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div></div>
                )}
                {(filters.workTypes.size > 0 || filters.seniorities.size > 0 || filters.teams.size > 0 || filters.roleAttributes.size > 0 || filters.compensation.size > 0 || filters.selectedLocation !== null || filters.jobTitleSearch !== '' || filters.popularQuery !== null || filters.stages.size > 0) && (
                    <button
                        onClick={() => {
                            setFilters({
                                workTypes: new Set(),
                                hasSalary: false,
                                seniorities: new Set(),
                                teams: new Set(),
                                roleAttributes: new Set(),
                                compensation: new Set(),
                                selectedLocation: null,
                                jobTitleSearch: '',
                                popularQuery: null,
                                stages: new Set()
                            })
                            onLocationSelect(null)
                            navigate('/', { replace: true })
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* Jobs List */}
            {isWaitingForFullDataset ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        <p className="text-gray-700 font-medium">
                            Loading jobs for {filters.selectedLocation?.name}...
                        </p>
                        <p className="text-sm text-gray-500">
                            Searching through {totalJobCount.toLocaleString()} opportunities
                        </p>
                    </div>
                </div>
            ) : filteredJobs.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
                    No jobs match your filters. Try adjusting your criteria.
                </div>
            ) : (
                <div className="flex flex-col gap-4 pb-8">
                    {displayedJobs.map((job) => {
                        const isExpanded = expandedJobId === job.id

                        // Determine if title + company is too long for salary badge on same line
                        const combinedLength = job.job_title.length + job.company_name_full.length
                        const shouldMoveSalaryToSecondLine = combinedLength > 80

                        return (
                    <div
                        key={job.id}
                        className={cn(
                            "group relative p-4 sm:p-4 rounded-xl transition-all duration-500 ease-in-out cursor-pointer",
                            "border border-gray-200 bg-white",
                            isExpanded && "shadow-[0_8px_30px_rgba(0,0,0,0.12)][0_8px_30px_rgba(255,255,255,0.1)]"
                        )}
                        style={{ contentVisibility: 'auto', containIntrinsicSize: '0 200px' }}
                        onClick={(e) => {
                            e.stopPropagation()
                            setExpandedJobId(isExpanded ? null : job.id)
                        }}
                    >

                    <div className="relative flex flex-col">
                        {/* Header: Logo + Title + Posted Date */}
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                                {/* Company Logo or Initials */}
                                {job.logo_url && !failedImages.has(job.id) ? (
                                    <img
                                        src={job.logo_url}
                                        alt={job.company_name_full}
                                        width="40"
                                        height="40"
                                        loading="lazy"
                                        className="w-10 h-10 sm:w-10 sm:h-10 rounded-md object-contain bg-white shrink-0"
                                        style={{ aspectRatio: '1' }}
                                        onError={() => handleImageError(job.id)}
                                    />
                                ) : (
                                    <div className="w-10 h-10 sm:w-10 sm:h-10 rounded-md flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-white text-xs font-semibold shrink-0">
                                        {getCompanyInitials(job.company_name_full)}
                                    </div>
                                )}

                                {/* Job Title + Company */}
                                <div className="flex-1 min-w-0">
                                    {/* Row 1: Job Title + Salary */}
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h3 className="font-medium text-gray-900 tracking-tight text-base leading-snug">
                                            {job.job_title}
                                        </h3>

                                        {/* Salary Badge */}
                                        {(job.salary_min || job.salary_max) && (
                                            <span className="px-2 py-1 rounded-md bg-green-50 text-green-700 text-xs font-medium">
                                                {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                                            </span>
                                        )}
                                    </div>

                                    {/* Row 2: Different layouts for mobile vs desktop */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
                                        {/* Mobile Layout: Company + Job Family, then Location + Work Setting */}
                                        <div className="sm:hidden flex flex-col gap-1">
                                            {/* Line 1: Company Name + Job Family */}
                                            <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
                                                <span>
                                                    {job.company_name_full}
                                                </span>
                                                {job.job_family && (
                                                    <>
                                                        <span className="text-gray-300">•</span>
                                                        <span className="flex items-center gap-1">
                                                            {getJobFamilyIcon(job.job_family)}
                                                            {job.job_family}
                                                        </span>
                                                    </>
                                                )}
                                            </div>

                                            {/* Line 2: Location + Work Setting Badge */}
                                            <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                                                    <span>{formatLocation(job)}</span>
                                                </span>
                                                {job.work_setting && (
                                                    <span className="px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium inline-flex items-center gap-1 shrink-0">
                                                        {job.work_setting.toLowerCase() === 'remote' && <Globe className="w-3 h-3" />}
                                                        {job.work_setting.toLowerCase() === 'hybrid' && <Train className="w-3 h-3" />}
                                                        {job.work_setting.toLowerCase() === 'onsite' && <Briefcase className="w-3 h-3" />}
                                                        {job.work_setting}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Desktop Layout: Company Name */}
                                        <span className="hidden sm:block text-sm text-gray-600">
                                            {job.company_name_full}
                                        </span>

                                        {/* Desktop separator */}
                                        <span className="hidden sm:inline text-gray-300">•</span>

                                        {/* Desktop: Job Family + Location + Work Setting - all in one row */}
                                        <div className="hidden sm:flex items-center gap-2 flex-wrap flex-1">
                                            {/* Job Family + Location */}
                                            <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
                                                {/* Job Family */}
                                                {job.job_family && (
                                                    <>
                                                        <span className="flex items-center gap-1">
                                                            {getJobFamilyIcon(job.job_family)}
                                                            {job.job_family}
                                                        </span>
                                                        <span className="text-gray-300">•</span>
                                                    </>
                                                )}

                                                {/* Location */}
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                                                    <span>{formatLocation(job)}</span>
                                                </span>
                                            </div>

                                            {/* Work Setting Badge - shown on desktop */}
                                            {job.work_setting && (
                                                <span className="inline-flex px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium items-center gap-1 shrink-0">
                                                    {job.work_setting.toLowerCase() === 'remote' && <Globe className="w-3 h-3" />}
                                                    {job.work_setting.toLowerCase() === 'hybrid' && <Train className="w-3 h-3" />}
                                                    {job.work_setting.toLowerCase() === 'onsite' && <Briefcase className="w-3 h-3" />}
                                                    {job.work_setting}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Apply Badge + Posted Date */}
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (job.job_url) {
                                            window.open(job.job_url, '_blank', 'noopener,noreferrer')
                                        }
                                    }}
                                    className="text-xs font-medium px-2 py-1 rounded-md bg-black text-white transition-opacity duration-300 hover:opacity-90 cursor-pointer inline-flex items-center gap-1"
                                >
                                    <RiFlashlightFill className="w-3.5 h-3.5" />
                                    apply
                                </button>
                                <span className="text-xs font-medium px-2 py-1 rounded-lg backdrop-blur-sm bg-black/5 text-gray-600 transition-colors duration-300 group-hover:bg-black/10:bg-white/20">
                                    {formatPostedDate(job.created_at)}
                                </span>
                            </div>
                        </div>

                        {/* Expanded Section - Description & Apply Button */}
                        <div
                            className={cn(
                                "grid transition-all duration-500 ease-in-out",
                                isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                            )}
                        >
                            <div className="overflow-hidden">
                                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 space-y-3 sm:space-y-4 sm:px-3">
                                    {/* Role Statement - Full Width */}
                                    {job.role_statement && (
                                        <div className="space-y-2 relative">
                                            <p className="text-sm text-gray-600 leading-relaxed font-normal">
                                                {job.role_statement}
                                                {job.work_setting_reasoning && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setShowWorkSettingReasoning(
                                                                showWorkSettingReasoning === job.id ? null : job.id
                                                            )
                                                        }}
                                                        className="inline-flex items-center text-gray-400 hover:text-gray-600 transition-colors align-middle ml-1"
                                                    >
                                                        <HelpCircle className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </p>
                                            {job.work_setting_reasoning && showWorkSettingReasoning === job.id && (
                                                <div className="absolute left-0 right-0 top-full mt-2 flex justify-center z-50">
                                                    <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                                                        <div className="space-y-3">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <h3 className="text-sm font-semibold text-gray-900">
                                                                        Location Info
                                                                    </h3>
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-xs font-normal">
                                                                        <Sparkles className="w-3 h-3" />
                                                                        AI generated
                                                                    </span>
                                                                </div>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setShowWorkSettingReasoning(null)
                                                                    }}
                                                                    className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                                {job.work_setting_reasoning}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Two Column Layout */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-3">
                                        {/* Left Column: Role Details */}
                                        {(job.seniority || job.job_family || job.technical_role || job.people_manager || job.travel_required || job.years_experience || job.education || job.timezone_required) && (
                                            <div className="space-y-2">
                                                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Role Details</h4>

                                                {/* Role summary sentence */}
                                                {job.seniority && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <Smile className="w-4 h-4 shrink-0" />
                                                        <span>{job.seniority} role{job.years_experience ? ` (${job.years_experience}+ years)` : ''}</span>
                                                    </p>
                                                )}

                                                {/* Travel required */}
                                                {job.travel_required && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <Plane className="w-4 h-4 shrink-0" />
                                                        <span>Travel required</span>
                                                    </p>
                                                )}

                                                {/* Education required */}
                                                {job.education && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <GraduationCap className="w-4 h-4 shrink-0" />
                                                        <span>Education required</span>
                                                    </p>
                                                )}

                                                {/* Technical role */}
                                                {job.technical_role && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <Cpu className="w-4 h-4 shrink-0" />
                                                        <span>Technical role</span>
                                                    </p>
                                                )}

                                                {/* People Manager */}
                                                {job.people_manager && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <UsersRound className="w-4 h-4 shrink-0" />
                                                        <span>People Manager</span>
                                                    </p>
                                                )}

                                                {/* Individual Contributor */}
                                                {job.people_manager === false && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <Blocks className="w-4 h-4 shrink-0" />
                                                        <span>Individual Contributor</span>
                                                    </p>
                                                )}

                                                {/* Variable compensation */}
                                                {(job.equity || job.bonus || job.variable_compensation) && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <PiggyBank className="w-4 h-4 shrink-0" />
                                                        <span>
                                                            {(() => {
                                                                const items = [
                                                                    job.equity && 'Equity',
                                                                    job.bonus && 'Bonus',
                                                                    job.variable_compensation && 'Variable Compensation'
                                                                ].filter(Boolean);

                                                                if (items.length === 1) return items[0];
                                                                if (items.length === 2) return items.join(' and ');
                                                                return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
                                                            })()} offered
                                                        </span>
                                                    </p>
                                                )}

                                                {/* Job Details - Attributes */}
                                                {job.timezone_required && (
                                                    <div className="flex flex-wrap gap-2">
                                                        <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-900">
                                                            {job.timezone_required}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Right Column: Company Info */}
                                        {(job.description || job.website_url || job.linkedin_url || job.employee_range || job.funding_stage || job.total_funding || job.active_jobs || job.founders || job.investors) && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">About {job.company_name_full}</h4>
                                                    {job.active_jobs && job.active_jobs >= 1 && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                if (job.company_name) {
                                                                    window.open(`/company/${encodeURIComponent(job.company_name)}`, '_blank', 'noopener,noreferrer')
                                                                }
                                                            }}
                                                            className="inline-flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors cursor-pointer"
                                                            title="View all open jobs"
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                                            <span className="text-xs">{job.active_jobs} open {job.active_jobs === 1 ? 'job' : 'jobs'}</span>
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Company short description */}
                                                {job.description_short && (
                                                    <p className="text-sm text-gray-600 leading-relaxed">
                                                        {job.description_short}
                                                        {job.website_url && (
                                                            <a
                                                                href={job.website_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="inline-flex text-gray-500 hover:text-blue-600 transition-colors ml-1.5"
                                                                title="Visit website"
                                                            >
                                                                <Link className="w-3.5 h-3.5" />
                                                            </a>
                                                        )}
                                                        {job.linkedin_url && (
                                                            <a
                                                                href={job.linkedin_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="inline-flex text-gray-500 hover:text-blue-600 transition-colors ml-1.5"
                                                                title="View LinkedIn"
                                                            >
                                                                <RiLinkedinFill className="w-3.5 h-3.5" />
                                                            </a>
                                                        )}
                                                    </p>
                                                )}

                                                {/* Company Stage & Size */}
                                                {(job.funding_stage && job.funding_stage?.toLowerCase() !== 'unknown') && (
                                                    <p className="text-sm text-gray-600 leading-relaxed flex items-center gap-1.5">
                                                        <RiInformationLine className="w-4 h-4 shrink-0" />
                                                        <span>
                                                            {formatFundingStage(job.funding_stage)}
                                                            {job.employee_range && job.employee_range?.toLowerCase() !== 'unknown' && ` with team of ${job.employee_range}`}
                                                        </span>
                                                    </p>
                                                )}


                                                {job.founders && (
                                                    <div className="space-y-1.5 text-xs">
                                                        {job.founders && typeof job.founders === 'string' && job.founders.toLowerCase() !== 'unknown' && (
                                                            <div>
                                                                <span className="text-gray-500">Founded by:</span>
                                                                <span className="ml-1.5 text-gray-700">{job.founders}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Report Incorrect Data */}
                                                <div className="pt-2 border-t border-gray-100 text-right">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            handleReportJob(job)
                                                        }}
                                                        disabled={reportedJobs.has(job.id) || reportingJobs.has(job.id)}
                                                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Flag className="w-3 h-3" />
                                                        {reportedJobs.has(job.id)
                                                            ? 'report submitted'
                                                            : reportingJobs.has(job.id)
                                                            ? 'submitting...'
                                                            : 'report incorrect company/job data'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>
                        )
                    })}

                    {/* Load More Button */}
                    {hasMore && (
                        <div className="py-8 flex justify-center">
                            <button
                                onClick={loadMoreJobs}
                                disabled={isLoadingMore}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-normal transition-colors border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoadingMore ? 'Loading...' : 'Load More'}
                            </button>
                        </div>
                    )}

                    {/* End of results indicator */}
                    {!hasMore && displayedJobs.length > JOBS_PER_PAGE && (
                        <div className="py-8 text-center text-sm text-gray-500">
                            You've reached the end of the results
                        </div>
                    )}
                </div>
            )}
            </div>
        </div>
    )
}

export default JobsGrid
